import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, categoryMatches, categoryArticles } from '@/mocks/category';
import { Helmet } from '@/pages/home/components/Helmet';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const activeSlug = slug || 'live';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [activeSlug]);

  const currentCategory = useMemo(() => categories.find((c) => c.slug === activeSlug), [activeSlug]);
  const matches = useMemo(() => categoryMatches[activeSlug] || [], [activeSlug]);
  const isNews = activeSlug === 'news';

  const categoryTitle = currentCategory?.label || '赛事直播';

  return (
    <>
      <Helmet
        title={`${categoryTitle} - 米乐体育官方平台`}
        description={`米乐体育${categoryTitle}频道，提供全球顶级${categoryTitle}赛事直播、实时比分、竞猜赔率和专业数据分析。`}
        keywords={`米乐体育,${categoryTitle},${categoryTitle}直播,${categoryTitle}竞猜,${categoryTitle}赛事`}
        canonical={`https://www.milesports.com/category/${activeSlug}`}
      />
      <Navbar />
      <main className="pt-16 md:pt-18">
        {/* Breadcrumb */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-400">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium">{categoryTitle}</span>
          </nav>
        </div>

        {/* Category Tabs */}
        <div className="bg-background-50 border-b border-background-200/70 sticky top-16 md:top-18 z-40">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => navigate(`/category/${cat.slug}`)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          {isNews ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <span className="text-[10px] font-bold text-accent-500 bg-accent-50 px-2 py-0.5 rounded-full">
                      {article.category}
                    </span>
                    <h3 className="font-bold text-foreground-900 text-sm mt-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-2 line-clamp-2">{article.summary}</p>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                      <span>{article.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line text-xs"></i>{(article.views / 10000).toFixed(1)}万
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <>
              {matches.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-20 h-20 rounded-full bg-background-100 mx-auto flex items-center justify-center mb-4">
                    <i className="ri-inbox-line text-3xl text-foreground-300"></i>
                  </div>
                  <p className="text-foreground-500">暂无赛事数据</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead>
                      <tr className="border-b border-background-200/70">
                        <th className="text-left text-xs font-medium text-foreground-400 uppercase tracking-wider py-3 px-4">赛事</th>
                        <th className="text-left text-xs font-medium text-foreground-400 uppercase tracking-wider py-3 px-4">联赛</th>
                        <th className="text-center text-xs font-medium text-foreground-400 uppercase tracking-wider py-3 px-4">时间</th>
                        <th className="text-center text-xs font-medium text-foreground-400 uppercase tracking-wider py-3 px-4">状态</th>
                        <th className="text-center text-xs font-medium text-foreground-400 uppercase tracking-wider py-3 px-4">主胜</th>
                        {matches.some((m) => m.drawOdds > 0) && (
                          <th className="text-center text-xs font-medium text-foreground-400 uppercase tracking-wider py-3 px-4">平局</th>
                        )}
                        <th className="text-center text-xs font-medium text-foreground-400 uppercase tracking-wider py-3 px-4">客胜</th>
                        <th className="text-center text-xs font-medium text-foreground-400 uppercase tracking-wider py-3 px-4">人气</th>
                        <th className="text-center text-xs font-medium text-foreground-400 uppercase tracking-wider py-3 px-4">操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matches.map((match) => (
                        <tr key={match.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                          <td className="py-4 px-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-foreground-900">{match.homeTeam}</span>
                              <span className="text-xs text-foreground-400">VS</span>
                              <span className="text-sm font-bold text-foreground-900">{match.awayTeam}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-xs font-medium text-foreground-600 bg-background-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                              {match.league}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="text-xs text-foreground-600 whitespace-nowrap">{match.startTime}</span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            {match.status === 'live' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-primary-500 bg-primary-50 px-2.5 py-1 rounded-full">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse"></span>
                                直播中 {match.score}
                              </span>
                            ) : (
                              <span className="text-xs font-medium text-accent-500 bg-accent-50 px-2.5 py-1 rounded-full">
                                即将开始
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="font-heading text-sm font-bold text-foreground-900">{match.homeOdds.toFixed(2)}</span>
                          </td>
                          {matches.some((m) => m.drawOdds > 0) && (
                            <td className="py-4 px-4 text-center">
                              {match.drawOdds > 0 ? (
                                <span className="font-heading text-sm font-bold text-foreground-600">{match.drawOdds.toFixed(2)}</span>
                              ) : (
                                <span className="text-xs text-foreground-300">-</span>
                              )}
                            </td>
                          )}
                          <td className="py-4 px-4 text-center">
                            <span className="font-heading text-sm font-bold text-foreground-900">{match.awayOdds.toFixed(2)}</span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            {match.viewers > 0 ? (
                              <span className="text-xs text-foreground-400">{(match.viewers / 10000).toFixed(0)}万</span>
                            ) : (
                              <span className="text-xs text-foreground-300">-</span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-center">
                            <button className="px-4 py-1.5 rounded-full text-xs font-bold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                              竞猜
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}